import React, { useMemo } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AppsIcon from '@mui/icons-material/Apps';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import type { SvgIconComponent } from '@mui/icons-material';
import { APPS, type AppConfig } from 'ui-cross-app';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { useAuth } from '../hooks/useAuth';

type AppMeta = {
  description: string;
  icon: SvgIconComponent;
  accent?: string;
};

const APP_META: Record<string, AppMeta> = {
  centraldb: {
    description: 'Central database admin, CRUD and sync tooling.',
    icon: ManageAccountsIcon,
    accent: '#455a64',
  },
  optima: {
    description: 'Recruitment, resources, sessions and admin tools.',
    icon: WorkOutlineIcon,
  },
  saleslist: {
    description: 'Opportunity pipeline with table and board views.',
    icon: TableViewOutlinedIcon,
  },
  travel: {
    description: 'Travel declarations, vouchers and allowances.',
    icon: FlightTakeoffOutlinedIcon,
  },
  fleet: {
    description: 'Live fleet map, trips and driver tooling.',
    icon: DirectionsCarOutlinedIcon,
    accent: '#2c7be5',
  },
  housing: {
    description: 'Housing operations, placements and accommodation tools.',
    icon: HomeWorkOutlinedIcon,
    accent: '#f39c12',
  },
  stock: {
    description: 'Inventory and tooling stock management.',
    icon: Inventory2OutlinedIcon,
    accent: '#4a6572',
  },
  administration: {
    description: 'Document signing, approval tracking and delivery links.',
    icon: DescriptionOutlinedIcon,
    accent: '#2e7d32',
  },
};

const AppLauncherPage: React.FC = () => {
  const theme = useTheme();
  const apps: AppConfig[] = useMemo(() => APPS.filter(app => !!app.url), []);
  const { user } = useSelector((state: RootState) => (state as any).auth);
  const { logout } = useAuth();
  const roles = Array.isArray(user?.roles) ? user?.roles : [];

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[AppLauncherPage] user', {
      id: user?.id,
      username: user?.username,
      roles,
    });
  }

  const accessRoleByApp: Record<string, string> = {
    centraldb: 'access_centraldb',
    optima: 'access_optima',
    saleslist: 'access_saleslist',
    travel: 'access_travel',
    fleet: 'access_fleet',
    housing: 'access_housing',
    administration: 'access_documents',
    // stock is currently open to any authenticated user; add a role here when available
  };

  const visibleApps = apps.filter(app => {
    const req = accessRoleByApp[app.id];
    if (!req) return true;
    return roles.includes(req);
  });

  const betaCount = visibleApps.filter(app => app.beta).length;

  const openApp = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const stats = [
    {
      label: 'Apps available',
      value: visibleApps.length || apps.length,
      hint: 'Based on your access roles',
    },
    {
      label: 'Beta apps',
      value: betaCount,
      hint: 'These apps are still in development, feedback is welcome! Data may be reset regularly.',
    },
    {
      label: 'Roles detected',
      value: roles.length,
      hint:
        roles.length === 0
          ? 'No realm roles found'
          : `${roles.slice(0, 3).join(', ')}${roles.length > 3 ? ` +${roles.length - 3}` : ''}`,
    },
  ];

  const accountUrl = useMemo(() => {
    let keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL as string | undefined;
    const realm = (import.meta.env.VITE_KEYCLOAK_REALM as string) || 'optima';
    if (!keycloakUrl) {
      keycloakUrl =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:8080'
          : 'https://sso.pivotwise.nl';
    }
    return `${keycloakUrl.replace(/\/+$/, '')}/realms/${realm}/account`;
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #07140d 0%, #0f2914 45%, #0c1f11 100%)'
            : 'linear-gradient(135deg, #f4fbf6 0%, #e8f5eb 45%, #e2f2e6 100%)',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 10% 20%, ${alpha(theme.palette.primary.light, 0.25)} 0%, transparent 32%),
            radial-gradient(circle at 85% 10%, ${alpha('#6cd46c', 0.22)} 0%, transparent 28%),
            radial-gradient(circle at 20% 80%, ${alpha('#b7f3c7', 0.2)} 0%, transparent 38%)
          `,
          filter: 'blur(26px)',
          opacity: 0.9,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(120deg, ${alpha(theme.palette.common.white, 0.07)} 0%, transparent 32%),
            linear-gradient(75deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 25%)
          `,
          opacity: theme.palette.mode === 'dark' ? 0.2 : 0.35,
          mixBlendMode: 'screen',
        }}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
        <Stack spacing={4}>
          <Box
            sx={{
              position: 'relative',
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(
                theme.palette.secondary.main,
                0.18,
              )})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              boxShadow: '0 24px 70px rgba(10, 60, 36, 0.35)',
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                background: `
                  radial-gradient(circle at 85% 20%, ${alpha('#a6f0b8', 0.2)} 0%, transparent 40%),
                  radial-gradient(circle at 15% 80%, ${alpha(theme.palette.primary.light, 0.25)} 0%, transparent 38%)
                `,
                opacity: theme.palette.mode === 'dark' ? 0.35 : 0.5,
              }}
            />
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              sx={{ position: 'relative' }}
            >
              <Stack spacing={1.2} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'linear-gradient(135deg, #42a040, #2e7d2e)',
                      boxShadow: '0 10px 26px rgba(17, 79, 48, 0.45)',
                      color: '#fff',
                      fontWeight: 800,
                      letterSpacing: -0.2,
                    }}
                    aria-label="Optima Uitzendburo"
                  >
                    O
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      Optima Uitzendburo
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                      The Optiverse
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<AppsIcon />}
                    label="Optima Apps"
                    color="primary"
                    sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
                  />
                  <Chip label="Single Sign-On" color="secondary" variant="outlined" />
                </Stack>
                <Typography variant="body1" color="text.secondary" maxWidth={720}>
                  Pick which application you want to open. You are signed in and can access the applications
                  listed below based on your roles.
                </Typography>
              </Stack>

              {user ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.45 : 0.85),
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    boxShadow: '0 14px 40px rgba(15, 37, 74, 0.25)',
                  }}
                >
                  <Avatar>{(user.name || user.username || 'O').charAt(0).toUpperCase()}</Avatar>
                  <Box textAlign="left">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {user.name || user.username}
                    </Typography>
                    {user.email && (
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    aria-label="User info"
                    size="small"
                    onClick={() => {
                      alert(`User: ${user.username || user.name || 'N/A'}\nRoles: ${roles.join(', ') || 'none'}`);
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<ManageAccountsIcon fontSize="small" />}
                    onClick={() => openApp(accountUrl)}
                  >
                    Account portal
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={logout}
                    startIcon={<LogoutIcon fontSize="small" />}
                  >
                    Logout
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Grid container spacing={2} sx={{ mt: 3, position: 'relative' }}>
              {stats.map(stat => (
                <Grid item xs={12} sm={4} key={stat.label}>
                  <Box
                    sx={{
                      p: 2.25,
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.8),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      boxShadow: '0 10px 32px rgba(20, 92, 60, 0.18)',
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.hint}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {visibleApps.length === 0 ? (
            <Alert severity="warning">
              You are signed in but do not have access to any applications. Please contact an administrator.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {visibleApps.map(app => {
                const meta = APP_META[app.id] || {
                  description: 'Open this application.',
                  icon: AppsIcon,
                };
                const Icon = meta.icon;
                const accent = meta.accent || theme.palette.primary.main;
                const accessRole = accessRoleByApp[app.id];
                const destination = (() => {
                  try {
                    return new URL(app.url).hostname;
                  } catch {
                    return app.url;
                  }
                })();

                return (
                  <Grid item xs={12} sm={6} md={4} key={app.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        position: 'relative',
                        background: `linear-gradient(150deg, ${alpha(accent, 0.08)}, ${alpha(
                          theme.palette.background.paper,
                          0.92,
                        )})`,
                        border: `1px solid ${alpha(accent, 0.24)}`,
                        boxShadow: '0 18px 50px rgba(20, 92, 60, 0.26)',
                        overflow: 'hidden',
                        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: alpha(accent, 0.45),
                          boxShadow: '0 22px 60px rgba(20, 92, 60, 0.32)',
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => openApp(app.url)}
                        sx={{ height: '100%', alignItems: 'stretch', display: 'flex' }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 2,
                                display: 'grid',
                                placeItems: 'center',
                                background: `radial-gradient(circle at 30% 30%, ${alpha(
                                  accent,
                                  0.32,
                                )}, transparent 70%), ${alpha(accent, 0.16)}`,
                                boxShadow: `0 10px 30px ${alpha(accent, 0.35)}`,
                              }}
                            >
                              <Icon htmlColor={theme.palette.getContrastText('#ffffff')} />
                            </Box>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h6" fontWeight={800}>
                                  {app.label}
                                </Typography>
                                {app.beta ? (
                                  <Chip
                                    label="Beta"
                                    size="small"
                                    color="warning"
                                    icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                                    sx={{ fontWeight: 700 }}
                                  />
                                ) : null}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {accessRole ? `Requires ${accessRole}` : 'Open to all roles'}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            {meta.description}
                          </Typography>

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch" sx={{ mt: 'auto' }}>
                            <Button
                              variant="contained"
                              onClick={e => {
                                e.preventDefault();
                                openApp(app.url);
                              }}
                              sx={{
                                borderRadius: 999,
                                px: 2.5,
                                boxShadow: '0 10px 30px rgba(20, 92, 60, 0.25)',
                              }}
                              fullWidth
                            >
                              Open here
                            </Button>
                            <Button
                              variant="outlined"
                              endIcon={<NorthEastIcon />}
                              onClick={e => {
                                e.preventDefault();
                                window.open(app.url, '_blank', 'noopener,noreferrer');
                              }}
                              sx={{ borderRadius: 999, px: 2.25 }}
                              fullWidth
                            >
                              New tab
                            </Button>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default AppLauncherPage;
